import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  CancelOutlined,
  CheckCircleOutlineOutlined,
  CallSplitOutlined,
  EditOutlined,
  HorizontalRuleOutlined,
  PictureAsPdfOutlined,
  PlaylistAddCheckCircleOutlined,
  Refresh as RefreshIcon,
  ReceiptLongOutlined,
  TrendingDownOutlined,
  TrendingUpOutlined,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { hasPermission } from "../../config/permissionUtils";
import { useAuth } from "../../context/AuthContext";
import { inventoryService } from "../../services";
import ActiveGeneralInventoryAlert from "./components/ActiveGeneralInventoryAlert";
import ProductFormatSummary, { getProductPackagingText } from "./components/ProductFormatSummary";
import StockCountCrossDetailSummary from "./components/StockCountCrossDetailSummary";
import ValuedShortageReportDialog from "./components/ValuedShortageReportDialog";
import {
  formatStockCountNumber,
  formatStockCountReadableQty,
  getStockCountPresentationsFromItem,
} from "./stockCountQtyFormat";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const INTEGER_QTY_MESSAGE = "La cantidad debe ser un numero entero. Utilice el formato correspondiente.";
const isPositiveIntegerInput = (value) => /^[1-9]\d*$/.test(String(value || "").trim());
const hasDecimalSeparator = (value) => /[.,]/.test(String(value || ""));

const formatDisplayQty = (value, decimals = 2) => formatStockCountNumber(value, decimals);

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const getFormatName = (item) => item?.product_presentation?.product_format?.name || "unidad";

const isMissingWithoutEntries = (item) => Boolean(item?.review_missing_without_entries);
const getDiffUnits = (item) => toNumber(item?.review_effective_diff_qty_base ?? item?.diff_qty_base);
const getNetDiff = (item) =>
  toNumber(item?.net_diff_qty_base ?? item?.review_effective_diff_qty_base ?? item?.diff_qty_base);

const formatSystemQty = (item) =>
  formatStockCountReadableQty(item?.system_qty_base, getStockCountPresentationsFromItem(item));
const formatCountedQty = (item) =>
  formatStockCountReadableQty(
    item?.review_effective_counted_qty_base ?? item?.counted_qty_base,
    getStockCountPresentationsFromItem(item)
  );
const formatDiffQty = (item) => {
  const diff = getDiffUnits(item);
  if (Math.abs(diff) <= 0.000001) {
    return formatStockCountReadableQty(0, getStockCountPresentationsFromItem(item));
  }
  return formatStockCountReadableQty(diff, getStockCountPresentationsFromItem(item), { showPlus: true });
};

const formatNetDiffQty = (item) =>
  formatStockCountReadableQty(getNetDiff(item), getStockCountPresentationsFromItem(item), { showPlus: true });

const buildSummary = (items) => {
  const counted = items.filter((item) => item?.is_counted).length;
  const withDiff = items.filter((item) => Math.abs(getNetDiff(item)) > 0.0001).length;
  const shortageProducts = items.filter(
    (item) => toNumber(item?.real_shortage_qty_base ?? 0) > 0 || getNetDiff(item) < -0.0001
  ).length;
  const surplusProducts = items.filter(
    (item) => toNumber(item?.real_surplus_qty_base ?? 0) > 0 || getNetDiff(item) > 0.0001
  ).length;
  return { counted, withDiff, shortageProducts, surplusProducts };
};

const missingWithoutEntriesTooltip =
  "Este producto no tiene cantidades registradas. Si confirmas, se tomara como faltante total.";

const hasPendingCashierSales = (item) => Boolean(item?.has_pending_cashier_sales);

const formatPendingCashierQty = (item) =>
  formatStockCountReadableQty(item?.pending_cashier_sales_qty_base, getStockCountPresentationsFromItem(item));

const formatEstimatedAfterPendingCashierQty = (item) =>
  formatStockCountReadableQty(
    item?.estimated_available_after_pending_cashier_qty_base,
    getStockCountPresentationsFromItem(item)
  );

const NOTE_STATUS_LABELS = {
  APPROVED: "Aprobada",
  DRAFT: "Borrador",
  CANCELED: "Cancelada",
};

const NOTE_VARIANT_LABELS = {
  COUNT_SURPLUS: "Sobrante de inventario",
  COUNT_SHORTAGE: "Faltante de inventario",
  CROSS_IN: "Entrada por cruce",
  CROSS_OUT: "Salida por cruce",
  NORMAL: "Normal",
};

const NOTE_VARIANT_DESCRIPTIONS = {
  COUNT_SURPLUS: "Productos con sobrante real despues de cruces.",
  COUNT_SHORTAGE: "Productos con faltante real despues de cruces.",
  CROSS_IN: "Producto sobrante utilizado para cubrir faltantes.",
  CROSS_OUT: "Producto faltante cubierto mediante conciliacion.",
};

const NOTE_ORIGIN_LABELS = {
  SELECTIVE_COUNT: "Conteo selectivo",
  GENERAL_COUNT: "Inventario general",
  MANUAL: "Manual",
};

const getNoteStatusLabel = (status) => NOTE_STATUS_LABELS[String(status || "").toUpperCase()] || "-";
const getNoteVariantLabel = (variant) => NOTE_VARIANT_LABELS[String(variant || "").toUpperCase()] || "-";
const getNoteVariantDescription = (variant) => NOTE_VARIANT_DESCRIPTIONS[String(variant || "").toUpperCase()] || "";
const getNoteOriginLabel = (origin) => NOTE_ORIGIN_LABELS[String(origin || "").toUpperCase()] || "-";
const getNoteTypeLabel = (note) => `${note?.direction === "IN" ? "NE" : "NS"} ${getNoteVariantLabel(note?.note_variant)}`;
const getNoteCrossIds = (note) => [
  ...new Set(
    toArray(note?.items)
      .flatMap((item) => toArray(item?.stock_count_cross_ids))
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value) && value > 0)
  ),
];

function MetricCard({ label, value, icon, color = "primary.main" }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.2, "&:last-child": { pb: 1.2 } }}>
        <Stack direction="row" spacing={1.1} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: "action.hover",
              color,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SafeDataGridContainer({ children, sx = {} }) {
  return (
    <Box sx={{ width: "100%", minWidth: 0, overflowX: "auto" }}>
      <Box sx={{ width: "100%", minWidth: 900, ...sx }}>{children}</Box>
    </Box>
  );
}

function StockCountReviewPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const countId = Number(id);
  const { currentBranch, isSuperuser, effectivePerms = [] } = useAuth();
  const branchId = currentBranch?.id || null;
  const canViewSelective = hasPermission(effectivePerms, "inventory_selective_view", isSuperuser);
  const canReviewSelective = hasPermission(effectivePerms, "inventory_selective_review", isSuperuser);
  const canCountSelective = hasPermission(effectivePerms, "inventory_selective_count", isSuperuser);
  const canAdjustSelective = hasPermission(effectivePerms, "inventory_selective_adjust", isSuperuser);
  const canManageSelectiveCross = hasPermission(effectivePerms, "inventory_selective_cross", isSuperuser);
  const canApplySelective = hasPermission(effectivePerms, "inventory_selective_apply", isSuperuser);
  const canCancelSelective = hasPermission(effectivePerms, "inventory_selective_cancel", isSuperuser);
  const canViewGeneral = hasPermission(effectivePerms, "inventory_general_view", isSuperuser);
  const canReviewGeneral = hasPermission(effectivePerms, "inventory_general_review", isSuperuser);
  const canCountGeneral = hasPermission(effectivePerms, "inventory_general_count", isSuperuser);
  const canAdjustGeneral = hasPermission(effectivePerms, "inventory_general_adjust", isSuperuser);
  const canManageGeneralCross = hasPermission(effectivePerms, "inventory_general_cross", isSuperuser);
  const canApplyGeneral = hasPermission(effectivePerms, "inventory_general_apply", isSuperuser);
  const canCancelGeneral = hasPermission(effectivePerms, "inventory_general_cancel", isSuperuser);
  const canView =
    canViewSelective ||
    canReviewSelective ||
    canCountSelective ||
    canAdjustSelective ||
    canManageSelectiveCross ||
    canApplySelective ||
    canCancelSelective ||
    canViewGeneral ||
    canReviewGeneral ||
    canCountGeneral ||
    canAdjustGeneral ||
    canManageGeneralCross ||
    canApplyGeneral ||
    canCancelGeneral;
  const canManageLegacy = hasPermission(effectivePerms, "inventory_count_manage", isSuperuser);

  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [applyNote, setApplyNote] = useState("");
  const [applying, setApplying] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [activeTab, setActiveTab] = useState("SUMMARY");
  const [selectedSourceItemId, setSelectedSourceItemId] = useState("");
  const [selectedTargetItemId, setSelectedTargetItemId] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [targetFilter, setTargetFilter] = useState("");
  const [crossDraft, setCrossDraft] = useState({ product_presentation_id: "", qty_presentation: "", reason: "" });
  const [crossSaving, setCrossSaving] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportValuation, setReportValuation] = useState("COST");
  const [showMissingOnly, setShowMissingOnly] = useState(false);
  const [selectedNoteDetail, setSelectedNoteDetail] = useState(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  const loadCount = useCallback(async () => {
    if (!branchId || !Number.isInteger(countId) || countId <= 0) {
      setCount(null);
      return;
    }

    setLoading(true);
    try {
      const data = await inventoryService.getStockCount(countId, { branch_id: branchId });
      setCount(data || null);
    } catch (error) {
      setCount(null);
      const detail = error?.response?.data?.detail || "No se pudo cargar el conteo fisico.";
      showSnack(detail, "error");
    } finally {
      setLoading(false);
    }
  }, [branchId, countId, showSnack]);

  useEffect(() => {
    loadCount();
  }, [loadCount]);

  const items = useMemo(() => toArray(count?.items), [count?.items]);
  const crosses = useMemo(() => toArray(count?.crosses), [count?.crosses]);
  const selectiveNotes = useMemo(() => toArray(count?.selective_notes), [count?.selective_notes]);
  const missingWithoutEntriesItems = useMemo(
    () => items.filter((item) => isMissingWithoutEntries(item)),
    [items]
  );
  const pendingCashierItems = useMemo(
    () => items.filter((item) => hasPendingCashierSales(item)),
    [items]
  );
  const movedDuringCountItems = useMemo(
    () => items.filter((item) => Boolean(item?.has_stock_movements_after_count_started)),
    [items]
  );
  useEffect(() => {
    if (showMissingOnly && missingWithoutEntriesItems.length === 0) {
      setShowMissingOnly(false);
    }
  }, [missingWithoutEntriesItems.length, showMissingOnly]);
  const realShortageItems = useMemo(
    () => items.filter((item) => toNumber(item?.real_shortage_qty_base ?? item?.net_diff_qty_base) > 0),
    [items]
  );
  const surplusItems = useMemo(
    () => items.filter((item) => toNumber(item?.real_surplus_qty_base) > 0),
    [items]
  );
  const shortageItems = useMemo(
    () => items.filter((item) => toNumber(item?.real_shortage_qty_base) > 0),
    [items]
  );
  const summary = useMemo(() => buildSummary(items), [items]);
  const countStatus = String(count?.status || "").toUpperCase();
  const isSelective = String(count?.count_type || "").toUpperCase() === "SELECTIVE";
  const isGeneral = String(count?.count_type || "").toUpperCase() === "GENERAL";
  const countKindLabel = isGeneral ? "inventario general" : "selectivo";
  const countOriginLabel = isGeneral ? "Inventario general" : "Conteo selectivo";
  const isReadOnly = countStatus === "APPLIED" || countStatus === "CLOSED" || countStatus === "CANCELED";
  const canEditCount = isSelective
    ? (canCountSelective && countStatus === "DRAFT") ||
      (canAdjustSelective && ["PENDING_REVIEW", "REVIEWING"].includes(countStatus))
    : isGeneral
      ? (canCountGeneral && countStatus === "DRAFT") ||
        (canAdjustGeneral && ["PENDING_REVIEW", "REVIEWING"].includes(countStatus))
      : canManageLegacy && countStatus === "DRAFT";
  const canConfirmNow = isSelective
    ? canApplySelective && ["PENDING_REVIEW", "REVIEWING"].includes(countStatus)
    : isGeneral
      ? canApplyGeneral && ["PENDING_REVIEW", "REVIEWING"].includes(countStatus)
      : canManageLegacy && countStatus === "DRAFT";
  const canCancelNow = isSelective
    ? canCancelSelective && ["DRAFT", "PENDING_REVIEW", "REVIEWING"].includes(countStatus)
    : isGeneral
      ? canCancelGeneral && ["DRAFT", "PENDING_REVIEW", "REVIEWING"].includes(countStatus)
      : false;
  const canManageCross = isSelective ? canManageSelectiveCross : isGeneral ? canManageGeneralCross : false;
  const activeGeneralInventory =
    isGeneral && ["DRAFT", "PENDING_REVIEW", "REVIEWING"].includes(countStatus) ? count : null;
  const canShowValuedReport =
    countStatus === "APPLIED" && ((isSelective && canApplySelective) || (isGeneral && canApplyGeneral));
  const isReadOnlyMode =
    (isSelective || isGeneral) &&
    !canEditCount &&
    !canConfirmNow &&
    !canCancelNow &&
    !canManageCross &&
    !canShowValuedReport;
  const summaryActionButtonSx = {
    height: 36,
    px: 1.4,
    minWidth: 150,
    justifyContent: "center",
    textTransform: "none",
    fontWeight: 700,
  };

  const buildEditPath = useCallback(
    (itemId = null) => {
      if (!count?.id) return null;
      const basePath =
        count?.count_type === "GENERAL"
          ? "/inventory/counts/general/capture"
          : "/inventory/counts/selective/new";
      const params = new URLSearchParams({ count_id: String(count.id) });
      if (itemId) params.set("item_id", String(itemId));
      return `${basePath}?${params.toString()}`;
    },
    [count?.count_type, count?.id]
  );

  const itemLabel = useCallback((item) => {
    const product = item?.product_presentation?.product;
    return `${product?.description || "Producto"}${product?.code ? ` (${product.code})` : ""}`;
  }, []);

  const reportRows = useMemo(
    () => realShortageItems.map((item, index) => {
      const qtyBase = toNumber(item?.real_shortage_qty_base);
      const qtyInBase = Math.max(toNumber(item?.product_presentation?.qty_in_base), 1);
      const price =
        reportValuation === "SALE"
          ? toNumber(item?.product_presentation?.base_sale_price)
          : toNumber(item?.product_presentation?.cost_price);
      const units = qtyBase / qtyInBase;
      return {
        number: index + 1,
        product: item?.product_presentation?.product?.description || "Producto",
        code: item?.product_presentation?.product?.code || "-",
        packaging: getProductPackagingText(item?.product_presentation?.product),
        qty: formatStockCountReadableQty(qtyBase, getStockCountPresentationsFromItem(item)),
        price,
        total: units * price,
      };
    }),
    [realShortageItems, reportValuation]
  );
  const countForReport = useMemo(() => ({
    ...(count || {}),
    branch_name:
      currentBranch?.alias ||
      currentBranch?.name ||
      currentBranch?.legalName ||
      currentBranch?.business_name ||
      "Sucursal",
  }), [count, currentBranch]);

  const filteredSurplusItems = useMemo(() => {
    const query = sourceFilter.trim().toLowerCase();
    if (!query) return surplusItems;
    return surplusItems.filter((item) => itemLabel(item).toLowerCase().includes(query));
  }, [itemLabel, sourceFilter, surplusItems]);

  const filteredShortageItems = useMemo(() => {
    const query = targetFilter.trim().toLowerCase();
    if (!query) return shortageItems;
    return shortageItems.filter((item) => itemLabel(item).toLowerCase().includes(query));
  }, [itemLabel, shortageItems, targetFilter]);

  const selectedSourceItem = useMemo(
    () => surplusItems.find((item) => String(item.id) === String(selectedSourceItemId)) || null,
    [selectedSourceItemId, surplusItems]
  );
  const selectedTargetItem = useMemo(
    () => shortageItems.find((item) => String(item.id) === String(selectedTargetItemId)) || null,
    [selectedTargetItemId, shortageItems]
  );

  const crossPresentationOptions = useMemo(() => {
    const rows = [];
    const appendRows = (item, prefix) => {
      if (!item) return;
      const presentations = getStockCountPresentationsFromItem(item);
      presentations.forEach((presentation) => {
        const id = Number(presentation?.id);
        if (!Number.isInteger(id) || id <= 0) return;
        rows.push({
          id,
          label: `${prefix}: ${presentation?.label || presentation?.format_name || "Presentacion"}`,
          qty_in_base: toNumber(presentation?.qty_in_base) || 1,
        });
      });
    };
    appendRows(selectedSourceItem, "Origen");
    appendRows(selectedTargetItem, "Destino");
    const seen = new Set();
    return rows.filter((row) => {
      if (seen.has(row.id)) return false;
      seen.add(row.id);
      return true;
    });
  }, [selectedSourceItem, selectedTargetItem]);

  const selectedCrossPresentation = useMemo(
    () =>
      crossPresentationOptions.find(
        (presentation) => String(presentation.id) === String(crossDraft.product_presentation_id)
      ) ||
      crossPresentationOptions[0] ||
      null,
    [crossDraft.product_presentation_id, crossPresentationOptions]
  );
  const crossQtyBase = useMemo(() => {
    const qty = toNumber(crossDraft.qty_presentation);
    const qtyInBase = toNumber(selectedCrossPresentation?.qty_in_base) || 1;
    return qty * qtyInBase;
  }, [crossDraft.qty_presentation, selectedCrossPresentation?.qty_in_base]);
  const maxCrossBase = useMemo(() => {
    if (!selectedSourceItem || !selectedTargetItem) return 0;
    return Math.min(
      toNumber(selectedSourceItem?.real_surplus_qty_base),
      toNumber(selectedTargetItem?.real_shortage_qty_base)
    );
  }, [selectedSourceItem, selectedTargetItem]);
  const remainingSurplusBase = useMemo(
    () => Math.max(0, toNumber(selectedSourceItem?.real_surplus_qty_base) - crossQtyBase),
    [crossQtyBase, selectedSourceItem?.real_surplus_qty_base]
  );
  const remainingShortageBase = useMemo(
    () => Math.max(0, toNumber(selectedTargetItem?.real_shortage_qty_base) - crossQtyBase),
    [crossQtyBase, selectedTargetItem?.real_shortage_qty_base]
  );
  const maxCrossPresentationQty = useMemo(() => {
    const qtyInBase = toNumber(selectedCrossPresentation?.qty_in_base) || 1;
    return maxCrossBase / qtyInBase;
  }, [maxCrossBase, selectedCrossPresentation?.qty_in_base]);
  const crossValidationMessage = useMemo(() => {
    if (!selectedSourceItem || !selectedTargetItem) return "Selecciona un sobrante y un faltante.";
    if (!selectedCrossPresentation?.id) return "Selecciona un formato.";
    if (!isPositiveIntegerInput(crossDraft.qty_presentation)) return INTEGER_QTY_MESSAGE;
    if (crossQtyBase <= 0) return "Ingresa una cantidad mayor a 0.";
    if (crossQtyBase > maxCrossBase + 0.0001) return "La cantidad excede el sobrante o faltante disponible.";
    return "";
  }, [crossDraft.qty_presentation, crossQtyBase, maxCrossBase, selectedCrossPresentation?.id, selectedSourceItem, selectedTargetItem]);
  const crossPreview = useMemo(() => {
    if (crossValidationMessage || !selectedSourceItem || !selectedTargetItem) return "";
    return `Cruce propuesto: ${itemLabel(selectedSourceItem)} -> ${itemLabel(selectedTargetItem)}. Cantidad: ${formatStockCountReadableQty(
      crossQtyBase,
      getStockCountPresentationsFromItem(selectedSourceItem)
    )}. Saldo restante sobrante: ${formatStockCountReadableQty(
      remainingSurplusBase,
      getStockCountPresentationsFromItem(selectedSourceItem)
    )}. Saldo restante faltante: ${formatStockCountReadableQty(
      remainingShortageBase,
      getStockCountPresentationsFromItem(selectedTargetItem)
    )}.`;
  }, [
    crossQtyBase,
    crossValidationMessage,
    itemLabel,
    remainingShortageBase,
    remainingSurplusBase,
    selectedSourceItem,
    selectedTargetItem,
  ]);
  const automaticCrosses = useMemo(
    () => crosses.filter((cross) => String(cross?.cross_type || "").toUpperCase() === "AUTO"),
    [crosses]
  );
  const manualCrosses = useMemo(
    () => crosses.filter((cross) => String(cross?.cross_type || "").toUpperCase() !== "AUTO"),
    [crosses]
  );
  const activeCrosses = useMemo(
    () =>
      crosses.filter(
        (cross) =>
          String(cross?.status || "").toUpperCase() === "APPLIED" &&
          !["VOIDED", "AUTO_VOIDED"].includes(String(cross?.cross_status || "ACTIVE").toUpperCase())
      ).sort((left, right) => Number(left?.id || 0) - Number(right?.id || 0)),
    [crosses]
  );
  const crossRowsByItem = useMemo(() => {
    const grouped = new Map();
    const pushRow = (itemId, row) => {
      const key = String(itemId);
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(row);
    };
    activeCrosses.forEach((cross) => {
      pushRow(cross.source_item_id, {
        direction: "OUT",
        label: "Entrega",
        color: "info",
        relatedProduct: cross?.target_product?.description || "Destino",
        qtyBase: cross?.qty_base,
        cross,
      });
      pushRow(cross.target_item_id, {
        direction: "IN",
        label: "Recibe",
        color: "success",
        relatedProduct: cross?.source_product?.description || "Origen",
        qtyBase: cross?.qty_base,
        cross,
      });
    });
    return grouped;
  }, [activeCrosses]);
  const formatCrossQty = useCallback(
    (cross) => {
      const sourceItem = items.find((item) => String(item.id) === String(cross?.source_item_id));
      return formatStockCountReadableQty(
        cross?.qty_base,
        getStockCountPresentationsFromItem(sourceItem)
      );
    },
    [items]
  );
  const formatCrossRowQty = useCallback(
    (row, item) => formatStockCountReadableQty(row?.qtyBase, getStockCountPresentationsFromItem(item)),
    []
  );
  const currentGridRows = useMemo(() => {
    const baseRows =
      activeTab === "DIFFS"
        ? items.filter((item) => Math.abs(getNetDiff(item)) > 0.0001)
        : items;
    const rows = showMissingOnly ? baseRows.filter((item) => isMissingWithoutEntries(item)) : baseRows;
    return rows.map((item, index) => ({ ...item, __rowNumber: index + 1 }));
  }, [activeTab, items, showMissingOnly]);

  const readOnlyAlert = useMemo(() => {
    if (!isReadOnly) return null;
    if (countStatus === "APPLIED") {
      return (
        <Alert severity="success">
          Este conteo fue aplicado el {formatDateTime(count?.confirmed_at || count?.applied_at) || "-"}.
          {" "}No se pueden realizar mas modificaciones.
        </Alert>
      );
    }
    if (countStatus === "CANCELED") {
      return <Alert severity="info">Este conteo fue anulado.</Alert>;
    }
    return <Alert severity="info">Este conteo esta cerrado.</Alert>;
  }, [count?.applied_at, count?.confirmed_at, countStatus, isReadOnly]);

  const columns = useMemo(
    () => [
      {
        field: "__rowNumber",
        headerName: "#",
        width: 64,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        valueGetter: (_, row) => row?.__rowNumber || "",
      },
      {
        field: "product",
        headerName: "Producto",
        flex: 1,
        minWidth: 320,
        sortable: false,
        renderCell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, justifyContent: "center" }}>
            <ProductFormatSummary product={row?.product_presentation?.product} nameSx={{ fontWeight: 600 }} />
            {hasPendingCashierSales(row) || row?.has_stock_movements_after_count_started ? (
              <Stack direction="row" spacing={0.4} useFlexGap flexWrap="wrap" sx={{ mt: 0.3 }}>
                {hasPendingCashierSales(row) ? (
                  <Tooltip
                    title={`Pendiente en Caja: ${formatPendingCashierQty(row)}. Estimado disponible: ${formatEstimatedAfterPendingCashierQty(row)}.`}
                    arrow
                  >
                    <Chip size="small" color="warning" variant="outlined" label="Pendiente en Caja" />
                  </Tooltip>
                ) : null}
                {row?.has_stock_movements_after_count_started ? (
                  <Chip size="small" color="warning" label="Tuvo movimiento" />
                ) : null}
              </Stack>
            ) : null}
          </Box>
        ),
      },
      {
        field: "format",
        headerName: "Formato",
        width: 120,
        sortable: false,
        valueGetter: (_, row) => getFormatName(row),
      },
      {
        field: "system_qty_base",
        headerName: "Sistema",
        width: 140,
        sortable: false,
        renderCell: ({ row }) => (
          <Typography sx={{ fontFamily: "monospace", fontSize: "0.82rem" }}>
            {formatSystemQty(row)}
          </Typography>
        ),
      },
      {
        field: "counted_qty_base",
        headerName: "Contado",
        width: 140,
        sortable: false,
        renderCell: ({ row }) => {
          if (isMissingWithoutEntries(row)) {
            return (
              <Tooltip title={missingWithoutEntriesTooltip} arrow>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 800, color: "warning.main" }}>
                  Sin conteo
                </Typography>
              </Tooltip>
            );
          }
          return (
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "0.82rem",
                fontWeight: row?.is_counted ? 700 : 400,
              }}
            >
              {row?.is_counted ? formatCountedQty(row) : "-"}
            </Typography>
          );
        },
      },
      {
        field: "diff_qty_base",
        headerName: "Diferencia",
        width: 150,
        sortable: false,
        renderCell: ({ row }) => {
          if (!row?.is_counted && !isMissingWithoutEntries(row)) {
            return <Chip size="small" variant="outlined" label="Pendiente" />;
          }
          const diff = getDiffUnits(row);
          const content = (
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "0.82rem",
                fontWeight: 700,
                color:
                  diff > 0
                    ? "success.main"
                    : diff < 0
                      ? "error.main"
                      : "text.secondary",
              }}
            >
              {formatDiffQty(row)}
            </Typography>
          );
          return isMissingWithoutEntries(row) ? (
            <Tooltip title={missingWithoutEntriesTooltip} arrow>
              <span>{content}</span>
            </Tooltip>
          ) : content;
        },
      },
      {
        field: "crosses_qty",
        headerName: "Cruces",
        width: 160,
        sortable: false,
        renderCell: ({ row }) => {
          const incoming = toNumber(row?.crossed_in_qty_base);
          const outgoing = toNumber(row?.crossed_out_qty_base);
          const net = incoming - outgoing;
          if (Math.abs(net) <= 0.0001) return "-";
          return (
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "0.82rem",
                fontWeight: 700,
                color: net > 0 ? "success.main" : "info.main",
              }}
            >
              {formatStockCountReadableQty(net, getStockCountPresentationsFromItem(row), { showPlus: true })}
            </Typography>
          );
        },
      },
      {
        field: "cross_detail",
        headerName: "Cruce",
        minWidth: 190,
        flex: 0.6,
        sortable: false,
        renderCell: ({ row }) => {
          const rows = crossRowsByItem.get(String(row?.id)) || [];
          if (rows.length === 0) return "-";
          return <StockCountCrossDetailSummary rows={rows} item={row} formatQty={formatCrossRowQty} />;
        },
      },
      {
        field: "net_diff_qty_base",
        headerName: "Diferencia neta",
        width: 180,
        sortable: false,
        renderCell: ({ row }) => {
          if (!row?.is_counted && !isMissingWithoutEntries(row)) return "-";
          const diff = getNetDiff(row);
          return (
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "0.82rem",
                fontWeight: 700,
                color: diff > 0 ? "success.main" : diff < 0 ? "error.main" : "text.secondary",
              }}
            >
              {formatNetDiffQty(row)}
            </Typography>
          );
        },
      },
      {
        field: "state",
        headerName: "Estado",
        width: 110,
        sortable: false,
        renderCell: ({ row }) => {
          if (isMissingWithoutEntries(row)) {
            return (
              <Tooltip title={missingWithoutEntriesTooltip} arrow>
                <Chip size="small" color="error" variant="outlined" label="Sin conteo" />
              </Tooltip>
            );
          }
          return (
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: row?.is_counted ? "success.main" : "text.secondary" }}
            >
              {row?.is_counted ? "Contado" : "Pendiente"}
            </Typography>
          );
        },
      },
      {
        field: "action",
        headerName: "Detalle",
        width: 150,
        sortable: false,
        renderCell: ({ row }) => {
          if (isMissingWithoutEntries(row) && canEditCount) {
            return (
              <Button
                size="small"
                onClick={() => {
                  const targetPath = buildEditPath(row.id);
                  if (targetPath) navigate(targetPath);
                }}
              >
                Seguir contando
              </Button>
            );
          }
          return (
            <Button size="small" onClick={() => setActiveTab("STACK")}>
              Ver pila
            </Button>
          );
        },
      },
    ],
    [buildEditPath, canEditCount, crossRowsByItem, formatCrossRowQty, navigate]
  );

  if (!canView) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permisos para ver conteos fisicos.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.2}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <MainText
            title="Resumen del conteo"
            subtitle="Revision final antes de aplicar los ajustes al inventario"
            icon={PlaylistAddCheckCircleOutlined}
          />
          {isReadOnlyMode ? <Chip size="small" variant="outlined" label="Solo lectura" /> : null}
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }} sx={{ minWidth: { md: 520 } }}>
          <Box>
            {canCancelNow ? (
              <Tooltip title={`Anular este ${countKindLabel}. No modifica inventario.`} arrow>
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelOutlined fontSize="small" />}
                    sx={summaryActionButtonSx}
                    disabled={!count?.id || loading || applying || canceling}
                    onClick={async () => {
                      const confirmed = window.confirm("Este conteo quedara anulado y no modificara stock.");
                      if (!confirmed) return;
                      setCanceling(true);
                      try {
                        const updatedCount = await inventoryService.cancelStockCount(count.id, { branch_id: branchId });
                        setCount(updatedCount || null);
                        showSnack("Conteo anulado correctamente.", "success");
                      } catch (error) {
                        const detail = error?.response?.data?.detail || "No se pudo anular el conteo.";
                        showSnack(detail, "error");
                      } finally {
                        setCanceling(false);
                      }
                    }}
                  >
                    Anular
                  </Button>
                </span>
              </Tooltip>
            ) : null}
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon fontSize="small" />}
              sx={summaryActionButtonSx}
              disabled={loading}
              onClick={loadCount}
            >
              Refrescar
            </Button>
            {canEditCount && !isReadOnly ? (
              <Tooltip title="Volver a captura para corregir o agregar cantidades." arrow>
                <span>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<EditOutlined fontSize="small" />}
                    sx={summaryActionButtonSx}
                    disabled={!count?.id || loading}
                    onClick={() => {
                      const targetPath = buildEditPath();
                      if (targetPath) navigate(targetPath);
                    }}
                  >
                    Seguir contando
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {canShowValuedReport ? (
              <Tooltip title="Ver reporte valorizado del faltante aplicado." arrow>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<PictureAsPdfOutlined fontSize="small" />}
                    sx={summaryActionButtonSx}
                    disabled={!count?.id || loading}
                    onClick={() => setReportDialogOpen(true)}
                  >
                    Reporte valorizado
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {canConfirmNow ? (
              <Tooltip title="Aplicar definitivamente el ajuste y generar notas automaticas." arrow>
                <span>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleOutlineOutlined fontSize="small" />}
                    sx={summaryActionButtonSx}
                    disabled={!count?.id || loading || applying || isReadOnly}
                    onClick={() => {
                      if (isReadOnly) return;
                      setApplyDialogOpen(true);
                    }}
                  >
                    {isGeneral ? "Confirmar inventario" : "Confirmar ajuste"}
                  </Button>
                </span>
              </Tooltip>
            ) : null}
          </Stack>
        </Stack>
      </Stack>

      <ActiveGeneralInventoryAlert activeInventory={activeGeneralInventory} />

      {readOnlyAlert}
      {isSelective &&
      count?.settings_snapshot?.selective_allow_sales_during_count &&
      count?.has_stock_movements_after_created_at ? (
        <Alert severity="warning">
          Hay movimientos de stock posteriores al inicio de este conteo para productos incluidos. Revisa las diferencias antes de confirmar.
        </Alert>
      ) : null}
      {pendingCashierItems.length > 0 ? (
        <Alert severity="warning">
          Hay {pendingCashierItems.length} productos con ventas pendientes de cobro en Caja. Revisa el stock del sistema contra la disponibilidad estimada antes de confirmar.
        </Alert>
      ) : null}
      {movedDuringCountItems.length > 0 ? (
        <Alert severity="warning">
          {movedDuringCountItems.length} productos tuvieron movimiento durante el conteo.
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1.1,
        }}
      >
        <MetricCard
          label="Contados"
          value={summary.counted}
          icon={<CheckCircleOutlineOutlined fontSize="small" />}
          color="success.main"
        />
        <MetricCard
          label="Con diferencia"
          value={summary.withDiff}
          icon={<TrendingUpOutlined fontSize="small" />}
          color="warning.main"
        />
        <MetricCard
          label="Productos con faltante"
          value={summary.shortageProducts}
          icon={<TrendingDownOutlined fontSize="small" />}
          color="error.main"
        />
        <MetricCard
          label="Productos con sobrante"
          value={summary.surplusProducts}
          icon={<HorizontalRuleOutlined fontSize="small" />}
          color="success.main"
        />
      </Box>

      {missingWithoutEntriesItems.length > 0 ? (
        <Alert
          severity="warning"
          action={
            <Stack direction="row" spacing={0.8}>
              {showMissingOnly ? (
                <Button size="small" color="inherit" onClick={() => setShowMissingOnly(false)}>
                  Limpiar
                </Button>
              ) : null}
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  setActiveTab("SUMMARY");
                  setShowMissingOnly(true);
                }}
              >
                Ver productos
              </Button>
            </Stack>
          }
        >
          Hay {missingWithoutEntriesItems.length} productos sin conteo registrado. En la revision se tomaran como faltante total si confirmas el {countKindLabel}.
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
        >
          <Tab value="SUMMARY" label="Resumen" />
          <Tab value="DIFFS" label="Diferencias" />
          <Tab value="STACK" label="Pila de conteo" />
          <Tab value="CROSSES" label="Cruces" />
          <Tab value="NOTES" label="Notas generadas" />
        </Tabs>

        {["SUMMARY", "DIFFS"].includes(activeTab) ? (
          <SafeDataGridContainer sx={{ height: "calc(100vh - 350px)", minHeight: 440 }}>
            <DataGrid
              rows={currentGridRows}
              columns={columns}
              getRowId={(row) => row.id}
              getRowHeight={() => "auto"}
              loading={loading}
              disableRowSelectionOnClick
              pageSizeOptions={[20, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 20, page: 0 } },
              }}
              sx={{
                border: 0,
                "& .MuiDataGrid-cell": {
                  py: 0.8,
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#2C2C2A",
                  color: "#ffffff",
                },
                "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover": {
                  backgroundColor: "#2C2C2A !important",
                },
                "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator": {
                  color: "#ffffff !important",
                },
              }}
            />
          </SafeDataGridContainer>
        ) : null}

        {activeTab === "STACK" ? (
          <Stack spacing={1} sx={{ p: 1.2 }}>
            {items.map((item, itemIndex) => (
              <Paper key={item.id} variant="outlined" sx={{ p: 1 }}>
                <Stack spacing={0.8}>
                  <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
                    <Box sx={{ minWidth: 0, display: "flex", gap: 1, alignItems: "flex-start" }}>
                      <Typography variant="body2" sx={{ fontWeight: 900, color: "text.secondary", minWidth: 28 }}>
                        #{itemIndex + 1}
                      </Typography>
                      <Box sx={{ minWidth: 0 }}>
                      <ProductFormatSummary product={item?.product_presentation?.product} nameSx={{ fontWeight: 800 }} />
                      <Typography variant="caption" color="text.secondary">
                        Sistema {formatSystemQty(item)} | Contado {isMissingWithoutEntries(item) ? "Sin conteo" : item?.is_counted ? formatCountedQty(item) : "-"} | Neta {formatNetDiffQty(item)}
                      </Typography>
                      {hasPendingCashierSales(item) || item?.has_stock_movements_after_count_started ? (
                        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                          {hasPendingCashierSales(item) ? (
                            <Chip size="small" color="warning" variant="outlined" label={`Pendiente en Caja: ${formatPendingCashierQty(item)}`} />
                          ) : null}
                          {item?.has_stock_movements_after_count_started ? (
                            <Chip size="small" color="warning" label="Tuvo movimiento" />
                          ) : null}
                        </Stack>
                      ) : null}
                      </Box>
                    </Box>
                    {canEditCount ? (
                      <Button
                        size="small"
                        onClick={() => {
                          const targetPath = buildEditPath(item.id);
                          if (targetPath) navigate(targetPath);
                        }}
                      >
                        Seguir contando
                      </Button>
                    ) : null}
                  </Stack>
                  <Divider />
                  {toArray(item.entries).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Sin entradas capturadas.
                    </Typography>
                  ) : (
                    <Stack spacing={0.6}>
                      {toArray(item.entries).map((entry, entryIndex) => (
                        <Stack key={entry.id} direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={0.8}>
                          <Typography variant="body2">
                            #{entryIndex + 1} | {formatDisplayQty(entry?.qty_presentation, 3)} {entry?.product_presentation?.format_name || "Presentacion"} | {entry?.effective_group_name || entry?.group_name_snapshot || entry?.group_name || "Sin grupo"}
                          </Typography>
                          <Typography variant="caption" color={entry?.is_voided ? "error" : "text.secondary"}>
                            {entry?.is_voided ? `Anulada: ${entry?.void_reason || "-"}` : formatDateTime(entry?.created_at)}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : null}

        {activeTab === "CROSSES" ? (
          <Stack spacing={1.2} sx={{ p: 1.2 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 1.2 }}>
              <Paper variant="outlined" sx={{ p: 1.2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Sobrantes disponibles
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Filtrar sobrantes"
                    value={sourceFilter}
                    onChange={(event) => setSourceFilter(event.target.value)}
                  />
                  <Stack spacing={0.8} sx={{ maxHeight: 360, overflowY: "auto", pr: 0.5 }}>
                    {filteredSurplusItems.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Sin sobrantes disponibles.
                      </Typography>
                    ) : filteredSurplusItems.map((item) => (
                      <Paper
                        key={`surplus-${item.id}`}
                        variant="outlined"
                        onClick={
                          canManageCross && !isReadOnly
                            ? () => setSelectedSourceItemId(item.id)
                            : undefined
                        }
                        sx={{
                          p: 1,
                          cursor: canManageCross && !isReadOnly ? "pointer" : "default",
                          borderColor:
                            canManageCross && String(selectedSourceItemId) === String(item.id)
                              ? "success.main"
                              : "divider",
                          bgcolor:
                            canManageCross && String(selectedSourceItemId) === String(item.id)
                              ? "rgba(46, 125, 50, 0.08)"
                              : "background.paper",
                        }}
                      >
                        <Stack spacing={0.6}>
                          <ProductFormatSummary product={item?.product_presentation?.product} nameSx={{ fontWeight: 800 }} />
                          <Typography variant="caption" color="success.main" sx={{ fontWeight: 700 }}>
                            Sobrante: {formatStockCountReadableQty(item?.real_surplus_qty_base, getStockCountPresentationsFromItem(item))}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Disponible: {formatStockCountReadableQty(item?.real_surplus_qty_base, getStockCountPresentationsFromItem(item))}
                          </Typography>
                          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                            {getStockCountPresentationsFromItem(item).map((presentation) => (
                              <Chip
                                key={`surplus-format-${item.id}-${presentation.id}-${presentation.qty_in_base}`}
                                size="small"
                                variant="outlined"
                                label={`${presentation.format_name || "Presentacion"} x ${formatStockCountNumber(presentation.qty_in_base)}`}
                              />
                            ))}
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Faltantes disponibles
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Filtrar faltantes"
                    value={targetFilter}
                    onChange={(event) => setTargetFilter(event.target.value)}
                  />
                  <Stack spacing={0.8} sx={{ maxHeight: 360, overflowY: "auto", pr: 0.5 }}>
                    {filteredShortageItems.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Sin faltantes disponibles.
                      </Typography>
                    ) : filteredShortageItems.map((item) => (
                      <Paper
                        key={`shortage-${item.id}`}
                        variant="outlined"
                        onClick={
                          canManageCross && !isReadOnly
                            ? () => setSelectedTargetItemId(item.id)
                            : undefined
                        }
                        sx={{
                          p: 1,
                          cursor: canManageCross && !isReadOnly ? "pointer" : "default",
                          borderColor:
                            canManageCross && String(selectedTargetItemId) === String(item.id)
                              ? "error.main"
                              : "divider",
                          bgcolor:
                            canManageCross && String(selectedTargetItemId) === String(item.id)
                              ? "rgba(211, 47, 47, 0.08)"
                              : "background.paper",
                        }}
                      >
                        <Stack spacing={0.6}>
                          <ProductFormatSummary product={item?.product_presentation?.product} nameSx={{ fontWeight: 800 }} />
                          <Typography variant="caption" color="error.main" sx={{ fontWeight: 700 }}>
                            Faltante: {formatStockCountReadableQty(item?.real_shortage_qty_base, getStockCountPresentationsFromItem(item))}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Necesita: {formatStockCountReadableQty(item?.real_shortage_qty_base, getStockCountPresentationsFromItem(item))}
                          </Typography>
                          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                            {getStockCountPresentationsFromItem(item).map((presentation) => (
                              <Chip
                                key={`shortage-format-${item.id}-${presentation.id}-${presentation.qty_in_base}`}
                                size="small"
                                variant="outlined"
                                label={`${presentation.format_name || "Presentacion"} x ${formatStockCountNumber(presentation.qty_in_base)}`}
                              />
                            ))}
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Stack>
              </Paper>
            </Box>

            {canManageCross && !isReadOnly ? (
            <Paper variant="outlined" sx={{ p: 1.2 }}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <CallSplitOutlined fontSize="small" color="primary" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Crear cruce
                  </Typography>
                </Stack>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "1.3fr 1.3fr 1fr 0.8fr" }, gap: 1 }}>
                  <TextField
                    label="Sobrante seleccionado"
                    size="small"
                    value={selectedSourceItem ? itemLabel(selectedSourceItem) : ""}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Faltante seleccionado"
                    size="small"
                    value={selectedTargetItem ? itemLabel(selectedTargetItem) : ""}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    select
                    size="small"
                    label="Formato"
                    value={crossDraft.product_presentation_id || selectedCrossPresentation?.id || ""}
                    onChange={(event) => setCrossDraft((prev) => ({ ...prev, product_presentation_id: event.target.value }))}
                    disabled={!selectedSourceItem || !selectedTargetItem}
                  >
                    {crossPresentationOptions.map((presentation) => (
                      <MenuItem key={`cross-format-${presentation.id}`} value={presentation.id}>
                        {presentation.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    type="number"
                    size="small"
                    label="Cantidad"
                    value={crossDraft.qty_presentation}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      if (hasDecimalSeparator(nextValue)) {
                        showSnack(INTEGER_QTY_MESSAGE, "warning");
                        return;
                      }
                      setCrossDraft((prev) => ({ ...prev, qty_presentation: nextValue }));
                    }}
                    inputProps={{ min: 1, max: maxCrossPresentationQty || undefined, step: 1, inputMode: "numeric", pattern: "[0-9]*" }}
                    disabled={!selectedSourceItem || !selectedTargetItem}
                  />
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 1 }}>
                  <TextField
                    size="small"
                    label="Equivalente base"
                    value={formatStockCountNumber(crossQtyBase)}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    size="small"
                    label="Maximo cruzable"
                    value={selectedSourceItem && selectedTargetItem ? formatStockCountReadableQty(maxCrossBase, getStockCountPresentationsFromItem(selectedSourceItem)) : "-"}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    size="small"
                    label="Motivo"
                    value={crossDraft.reason}
                    onChange={(event) => setCrossDraft((prev) => ({ ...prev, reason: event.target.value }))}
                  />
                </Box>
                {crossPreview ? <Alert severity="info">{crossPreview}</Alert> : null}
                {crossValidationMessage ? <Alert severity="warning">{crossValidationMessage}</Alert> : null}
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    disabled={crossSaving || Boolean(crossValidationMessage)}
                    onClick={async () => {
                      if (!count?.id || !selectedSourceItem?.id || !selectedTargetItem?.id || !selectedCrossPresentation?.id) return;
                      setCrossSaving(true);
                      try {
                        await inventoryService.createStockCountCross(count.id, {
                          branch_id: branchId,
                          source_item_id: selectedSourceItem.id,
                          target_item_id: selectedTargetItem.id,
                          product_presentation_id: selectedCrossPresentation.id,
                          qty_presentation: crossDraft.qty_presentation,
                          reason: crossDraft.reason,
                        });
                        setCrossDraft({ product_presentation_id: "", qty_presentation: "", reason: "" });
                        await loadCount();
                        showSnack("Cruce manual creado.", "success");
                      } catch (error) {
                        showSnack(error?.response?.data?.detail || "No se pudo crear el cruce.", "error");
                      } finally {
                        setCrossSaving(false);
                      }
                    }}
                  >
                    Aplicar cruce
                  </Button>
                </Box>
              </Stack>
            </Paper>
            ) : null}

            {[
              { key: "auto", title: "Cruces automaticos aplicados", rows: automaticCrosses },
              { key: "manual", title: "Cruces manuales", rows: manualCrosses },
            ].map((section) => (
              <Stack key={section.key} spacing={0.8}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {section.title}
                </Typography>
                {section.rows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Sin registros.
                  </Typography>
                ) : (
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" }, gap: 1 }}>
                    {section.rows.map((cross) => (
                      <Paper key={cross.id} variant="outlined" sx={{ p: 1 }}>
                        <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between">
                          <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
                              <Chip size="small" color={cross.cross_type === "AUTO" ? "info" : "primary"} label={cross.cross_type === "AUTO" ? "Automatico" : "Manual"} />
                              <Chip size="small" variant="outlined" label={cross.status === "APPLIED" ? "Aplicado" : "Anulado"} />
                            </Stack>
                            <Stack spacing={0.3} sx={{ mt: 0.6 }}>
                              <ProductFormatSummary
                                product={cross?.source_product}
                                name={cross?.source_product?.description || "Origen"}
                                code={cross?.source_product?.code}
                                nameSx={{ fontWeight: 700 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                hacia
                              </Typography>
                              <ProductFormatSummary
                                product={cross?.target_product}
                                name={cross?.target_product?.description || "Destino"}
                                code={cross?.target_product?.code}
                                nameSx={{ fontWeight: 700 }}
                              />
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Cantidad: {formatCrossQty(cross)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {cross?.reason || (cross.cross_type === "AUTO" ? "Motivo automatico" : "Sin motivo")}
                            </Typography>
                          </Box>
                          {cross.status === "APPLIED" && canManageCross && !isReadOnly ? (
                            <Button
                              size="small"
                              color="error"
                              onClick={async () => {
                                try {
                                  const updated = await inventoryService.voidStockCountCross(count.id, cross.id, {
                                    branch_id: branchId,
                                    reason: "Anulado por supervisor",
                                  });
                                  setCount((prev) => ({
                                    ...prev,
                                    crosses: toArray(prev?.crosses).map((row) => row.id === updated.id ? updated : row),
                                  }));
                                  await loadCount();
                                  showSnack("Cruce anulado.", "success");
                                } catch (error) {
                                  showSnack(error?.response?.data?.detail || "No se pudo anular el cruce.", "error");
                                }
                              }}
                            >
                              Deshacer
                            </Button>
                          ) : null}
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Stack>
            ))}
          </Stack>
        ) : null}

        {activeTab === "NOTES" ? (
          <Stack spacing={1} sx={{ p: 1.2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Notas del {countKindLabel}
            </Typography>
            {selectiveNotes.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Aun no hay notas generadas. Se crearan al confirmar el {countKindLabel}.
              </Typography>
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1 }}>
                {selectiveNotes.map((note) => {
                  const isIn = note.direction === "IN";
                  const isCross = String(note.note_variant || "").includes("CROSS");
                  return (
                    <Paper key={`${note.direction}-${note.id}`} variant="outlined" sx={{ p: 1.1, borderColor: isCross ? "info.light" : isIn ? "success.light" : "warning.light" }}>
                      <Stack spacing={0.8}>
                        <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
                          <Chip size="small" color={isCross ? "info" : isIn ? "success" : "warning"} label={getNoteTypeLabel(note)} />
                          {note.auto_generated ? <Chip size="small" variant="outlined" label="Automatica" /> : null}
                          <Chip size="small" variant="outlined" label={getNoteStatusLabel(note.status)} />
                        </Stack>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {note.reference || `#${note.id}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {note.items_count || 0} productos | {getNoteOriginLabel(note.note_origin)} | {formatDateTime(note.created_at)}
                        </Typography>
                        {getNoteVariantDescription(note.note_variant) ? (
                          <Typography variant="caption" color="text.secondary">
                            {getNoteVariantDescription(note.note_variant)}
                          </Typography>
                        ) : null}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ReceiptLongOutlined fontSize="small" />}
                          onClick={() => setSelectedNoteDetail(note)}
                        >
                          Ver detalle
                        </Button>
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Stack>
        ) : null}
      </Paper>

      <ValuedShortageReportDialog
        open={canShowValuedReport && reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        count={countForReport}
        rows={reportRows}
        valuationMode={reportValuation}
        onChangeValuationMode={setReportValuation}
      />

      <Dialog
        open={Boolean(selectedNoteDetail)}
        onClose={() => setSelectedNoteDetail(null)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Detalle de nota</DialogTitle>
        <DialogContent dividers>
          {selectedNoteDetail ? (
            <Stack spacing={1.5}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1 }}>
                <Typography variant="body2">
                  <strong>Tipo de nota:</strong> {getNoteTypeLabel(selectedNoteDetail)}
                </Typography>
                <Typography variant="body2">
                  <strong>Referencia:</strong> {selectedNoteDetail.reference || `#${selectedNoteDetail.id}`}
                </Typography>
                <Typography variant="body2">
                  <strong>Estado:</strong> {getNoteStatusLabel(selectedNoteDetail.status)}
                </Typography>
                <Typography variant="body2">
                  <strong>Fecha:</strong> {formatDateTime(selectedNoteDetail.created_at) || "-"}
                </Typography>
                <Typography variant="body2">
                  <strong>Origen:</strong> {countOriginLabel} #{selectedNoteDetail.stock_count_id || count?.id || "-"}
                </Typography>
                <Typography variant="body2">
                  <strong>Variante:</strong> {selectedNoteDetail.note_variant || "-"}
                </Typography>
              </Box>

              {getNoteVariantDescription(selectedNoteDetail.note_variant) ? (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  {getNoteVariantDescription(selectedNoteDetail.note_variant)}
                </Alert>
              ) : null}

              {String(selectedNoteDetail.note_variant || "").includes("CROSS") ? (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Nota relacionada: {selectedNoteDetail.related_note?.reference || "Sin referencia"} ({getNoteTypeLabel(selectedNoteDetail.related_note)} #{selectedNoteDetail.related_note?.id || "-"})
                  {getNoteCrossIds(selectedNoteDetail).length > 0
                    ? ` | Cruces relacionados: ${getNoteCrossIds(selectedNoteDetail).map((crossId) => `#${crossId}`).join(", ")}`
                    : ""}
                </Alert>
              ) : null}

              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#2c2c2a" }}>
                    <TableCell sx={{ color: "#fff", fontWeight: 800 }}>#</TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Codigo</TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Producto</TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Presentacion</TableCell>
                    <TableCell align="right" sx={{ color: "#fff", fontWeight: 800 }}>Cantidad</TableCell>
                    <TableCell align="right" sx={{ color: "#fff", fontWeight: 800 }}>Equivalente base</TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Cruces relacionados</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {toArray(selectedNoteDetail.items).length > 0 ? (
                    toArray(selectedNoteDetail.items).map((item, index) => {
                      const productPayload = {
                        description: item?.product_description || "Producto",
                        code: item?.product_code || "",
                        max_presentation_name: item?.max_presentation_name,
                        max_presentation_qty_base: item?.max_presentation_qty_base,
                        presentations_summary: item?.presentations_summary,
                      };
                      const crossIds = toArray(item?.stock_count_cross_ids);
                      return (
                        <TableRow key={`${selectedNoteDetail.direction}-${selectedNoteDetail.id}-${item.id || index}`}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{item?.product_code || "-"}</TableCell>
                          <TableCell>
                            <ProductFormatSummary
                              product={productPayload}
                              name={item?.product_description || "Producto"}
                              code={item?.product_code || ""}
                            />
                          </TableCell>
                          <TableCell>{item?.presentation_name || "Presentacion"}</TableCell>
                          <TableCell align="right">{formatDisplayQty(item?.qty_presentation, 3)}</TableCell>
                          <TableCell align="right">{formatDisplayQty(item?.qty_base, 3)}</TableCell>
                          <TableCell>
                            {crossIds.length > 0 ? (
                              <Stack direction="row" spacing={0.4} useFlexGap flexWrap="wrap">
                                {crossIds.map((crossId) => (
                                  <Chip key={`note-cross-${item.id}-${crossId}`} size="small" variant="outlined" label={`#${crossId}`} />
                                ))}
                              </Stack>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7}>Sin productos en la nota.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="flex-end">
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  Total de productos: {selectedNoteDetail.total_products ?? toArray(selectedNoteDetail.items).length}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  Total cantidad base: {formatDisplayQty(selectedNoteDetail.total_qty_base, 3)}
                </Typography>
              </Stack>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedNoteDetail(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!isReadOnly && canConfirmNow && applyDialogOpen}
        onClose={() => {
          if (isReadOnly) return;
          setApplyDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {isSelective ? "Confirmar conteo selectivo" : isGeneral ? "Confirmar inventario general" : "Aplicar ajuste de inventario"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              {isSelective || isGeneral
                ? "Se generaran y aprobaran notas automaticas para sobrantes, faltantes y cruces. Esta accion no se puede deshacer desde esta pantalla."
                : "Se generaran movimientos de ajuste solo para los items contados con diferencia. Esta accion no se puede deshacer desde esta pantalla."}
            </Typography>
            <TextField
              label="Nota"
              multiline
              minRows={3}
              fullWidth
              value={applyNote}
              onChange={(event) => setApplyNote(event.target.value)}
              placeholder="Observacion del ajuste"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyDialogOpen(false)} disabled={applying}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={applying || isReadOnly || !canConfirmNow}
            onClick={async () => {
              if (!count?.id || !branchId || isReadOnly || !canConfirmNow) return;
              setApplying(true);
              try {
                const updatedCount = await inventoryService.applyStockCount(count.id, {
                  branch_id: branchId,
                  comment: applyNote,
                });
                setCount(updatedCount || null);
                setApplyDialogOpen(false);
                showSnack(isGeneral ? "Inventario general aplicado correctamente." : "Ajuste aplicado correctamente.", "success");
              } catch (error) {
                const detail = error?.response?.data?.detail || "No se pudo aplicar el ajuste.";
                showSnack(detail, "error");
              } finally {
                setApplying(false);
              }
            }}
          >
            {isGeneral ? "Confirmar inventario" : "Confirmar ajuste"}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}

export default StockCountReviewPage;
